import React from 'react';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { space } from 'styled-system';

import h24LogoSvg from 'common/assets/images/home-24-logo.svg';
import { Flex } from 'common/components/Flex';
import Spinner from 'common/components/Spinner';
import { isTouchDevice } from 'common/DeviceDetector';
import { useLocalization } from 'common/providers/LocaleProvider';
import type { SpaceProps } from 'common/theme/system-types';
import type { ObjectFitProps } from 'common/theme/system-utilities';
import { objectFit } from 'common/theme/system-utilities';
import { trackLogoClick } from './tracking';

interface SideNavHeaderTitleProps {
  isRootLevel: boolean;
  logoUrl?: string;
  shopName?: string;
  shopUrl?: string;
  isLoading?: boolean;
  isTabsEnabled?: boolean;
}

const HeaderContainer = styled.a<SpaceProps>`
  ${space};

  ${() =>
    isTouchDevice() &&
    css`
      &:focus {
        outline: none;
      }
    `}
`;

const LogoImage = styled.img<ObjectFitProps>`
  ${objectFit}
`;

const useContainerLinkStyles = (shopUrl?: string, logoUrl?: string) => {
  const { p } = useLocalization();
  if (shopUrl) {
    return { href: shopUrl };
  }

  return logoUrl ? ({ as: 'div' } as const) : { href: p.t('h24_home_url') };
};

const SideNavHeaderTitle = ({
  isRootLevel,
  shopName,
  shopUrl,
  logoUrl,
  isLoading = false,
  isTabsEnabled = false,
}: SideNavHeaderTitleProps) => {
  const { t } = useLocalization();
  const containerLinkStyles = useContainerLinkStyles(shopUrl, logoUrl);

  if (!isRootLevel) {
    return null;
  }

  return (
    <Flex alignItems="center">
      <HeaderContainer
        data-testid="sidenav-heading"
        p="sp_16"
        onClick={() => trackLogoClick(shopName)}
        {...containerLinkStyles}
      >
        <LogoImage
          src={logoUrl ?? h24LogoSvg}
          alt={shopName ?? t('h24_logo_alt_title')}
          width="auto"
          height={40}
          objectFit="contain"
        />
      </HeaderContainer>
      {isTabsEnabled && isLoading && <Spinner size={24} />}
    </Flex>
  );
};

export default SideNavHeaderTitle;
